import { Grid2, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import DoneIcon from "@mui/icons-material/Done";
import StatusBox from "../../../components/StatusBox";
import { useTranslate } from "../../../hooks/useTranslate";
import { useStudents } from "../../../context/students";
import useStatuses from "../../../context/status/useStatuses";
import LockedPage from "../../../components/LockedPage";
import VerificationStatusChip from "../../../components/VerificationStatusChip";
import { useGetPaymentPlans } from "../../../hooks/useGetPaymentPlans";
import axiosManager from "../../../services/axiosManager";
import SnackbarToast from "../../../components/SnackbarToast";
import { useToast } from "../../../context/toast";
import StartDateBlock from "./components/StartDateBlock";
import ContractBlock from "./components/ContractBlock";
import BillingBlock from "./components/BillingBlock";
import SummaryBox from "../../../components/SummaryBox";
import PaymentSummary from "./components/PaymentSummary";
import Preloader from "../../../components/Preloader";
import CheckoutForm from "./components/CheckoutForm";
import CheckoutStatus from "./components/CheckoutStatus";
import "./TuitionBilling.css";
import { useGetStartDates } from "../../../hooks/useGetStartDates";
import { formatTo } from "../../../utils/dateTimeHelper";
import { stripeAppearance, stripeFonts, stripePromise } from "../../../services/stripeConfig";
import { KEY_APPROVED, KEY_NULL, KEY_PROGRESS } from "../../../utils/getVerificationStatusKey";

const delayInMs = 3_000;

const TuitionBilling = () => {
    const toast = useToast();
    const { t } = useTranslate();
    const { activeStudent, activeStudentId } = useStudents();
    const {
        isLockedStatus: { tuitionBilling: isLocked }, statuses, generalStatuses,
    } = useStatuses();

    const [searchParams] = useSearchParams();

    const hasCompleted = statuses.tuitionBilling.isSuccess;
    const studentFullName = `${activeStudent.forename} ${activeStudent.surname}`;
    const hasPaymentComplete = searchParams.has("payment-complete");
    const isPaymentProcessing = generalStatuses?.tuitionPaymentStatus === "processing";

    const {
        isLoading, paymentPlans, refetch, isRefetching,
    } = useGetPaymentPlans(isLocked || hasPaymentComplete || hasCompleted ? undefined : activeStudentId);
    const {
        startDatesOptions, isLoading: isLoadingStartDates,
    } = useGetStartDates(isLocked || hasPaymentComplete || hasCompleted);

    const [activeBlock, setActiveBlock] = useState(activeStudent?.desiredStartDate ? "agreement" : "startDate");
    const [startDate, setStartDate] = useState(activeStudent?.desiredStartDate ? formatTo(activeStudent?.desiredStartDate, "yyyy-MM-dd") : undefined);
    const [agreement, setAgreement] = useState();
    const [paymentType, setPaymentType] = useState();

    const isRollingSelected = agreement === "Rolling";
    const isDisabled = !startDate || !agreement || !paymentType;

    const changeStartDate = (value) => {
        setStartDate(value);
        refetch();
    };
    const changeAgreement = (value) => {
        setAgreement(value);
        refetch();
    };

    const {
        mutateAsync: configureEnrollmentPayments,
        isPending: isConfiguring,
        error: errorConfigure,
        reset: resetConfigure,
        data: payment,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/Payment/${activeStudentId}/ConfigureEnrollmentPayments`)
            .then((response) => response?.data),
    });

    const hasPaymentInvoice = !hasPaymentComplete && !isPaymentProcessing && !!payment;
    const shouldPay = hasPaymentInvoice && payment?.invoice?.status === "draft";
    const wasPaidInInvoice = hasPaymentInvoice && payment?.invoice?.status === "paid";
    const shouldSelectOptions = !hasCompleted && !hasPaymentComplete && !isPaymentProcessing && !payment;

    useEffect(() => {
        if (activeStudentId) resetConfigure();
    }, [activeStudentId]);

    useEffect(() => {
        if (activeStudent?.desiredStartDate) {
            const startDateFormatted = formatTo(activeStudent.desiredStartDate, "yyyy-MM-dd");
            const hasStartInOptions = (startDatesOptions || []).some((it) => it.value === startDateFormatted);
            setStartDate(hasStartInOptions ? startDateFormatted : undefined);
            setActiveBlock(!hasStartInOptions ? "startDate" : (agreement ? "billing" : "agreement"));
        } else {
            setActiveBlock("startDate");
        }
    }, [activeStudent?.desiredStartDate, startDatesOptions?.length]);

    useEffect(() => {
        if (wasPaidInInvoice) {
            setTimeout(() => window.location.reload(), delayInMs);
        }
    }, [wasPaidInInvoice]);

    const paymentStatus = useMemo(() => {
        if (isPaymentProcessing) {
            return { key: KEY_PROGRESS, statusTextKey: "status.processing", isInfo: true };
        }
        return {
            key: KEY_NULL,
            statusTextKey: statuses.tuitionBilling.statusTextKey,
            isSuccess: (hasCompleted ? true : wasPaidInInvoice) || statuses.tuitionBilling.isSuccess,
            isWarning: (hasCompleted ? false : !wasPaidInInvoice) || statuses.tuitionBilling.isWarning,
            isInfo: statuses.tuitionBilling.isInfo,
        };
    }, [isPaymentProcessing, wasPaidInInvoice, statuses.tuitionBilling, hasCompleted]);

    const paymentSelected = (paymentPlans || []).find(
        (option) => option.type === paymentType,
    );
    const stripeOptions = useMemo(() => ({
        clientSecret: payment?.clientSecret,
        appearance: stripeAppearance,
        fonts: stripeFonts,
        loader: "auto",
    }), [payment?.clientSecret]);

    const onProceed = async () => {
        try {
            await configureEnrollmentPayments();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="page-content tuition-billing-page">
            <Grid2 container spacing={2} className="mb-8">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }}>
                    <Typography variant="h1">
                        {t("tuitionBilling.title")}
                    </Typography>
                    <Typography
                        variant="body1"
                        className="mt-4"
                        component="div"
                    >
                        {t("tuitionBilling.description")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <StatusBox
                        title={t("tuitionBilling.tuitionBillingStatus")}
                        isSuccess={paymentStatus.isSuccess}
                        isWarning={paymentStatus.isWarning}
                        isInfo={paymentStatus.isInfo}
                    >
                        <Typography variant="body2">{t("tuitionBilling.tuitionBillingStatusDescription")}</Typography>
                        <VerificationStatusChip
                            status={wasPaidInInvoice ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true } : paymentStatus}
                        />
                    </StatusBox>
                </Grid2>
            </Grid2>
            <LockedPage isLocked={hasPaymentComplete ? false : isLocked}>
                <Preloader isLoading={isLoading || isConfiguring} overflow showInitiallyOverflow>
                    {shouldSelectOptions && (
                        <div className="flex flex-column gap-8">
                            <StartDateBlock
                                isOpen={activeBlock === "startDate"}
                                onOpenClick={() => setActiveBlock("startDate")}
                                onClose={() => setActiveBlock("agreement")}
                                studentFullName={studentFullName}
                                startDatesOptions={startDatesOptions}
                                isLoadingStartDates={isLoadingStartDates}
                                startDate={startDate}
                                changeStartDate={changeStartDate}
                            />
                            <ContractBlock
                                isOpen={activeBlock === "agreement"}
                                onOpenClick={() => setActiveBlock("agreement")}
                                onClose={() => setActiveBlock("billing")}
                                studentFullName={studentFullName}
                                agreement={agreement}
                                changeAgreement={changeAgreement}
                                disabled={!startDate}
                            />
                            <BillingBlock
                                isOpen={activeBlock === "billing"}
                                onOpenClick={() => setActiveBlock("billing")}
                                onClose={() => setActiveBlock(undefined)}
                                isRefetching={isRefetching}
                                studentFullName={studentFullName}
                                agreement={agreement}
                                paymentPlans={paymentPlans}
                                paymentType={paymentType}
                                changePaymentType={setPaymentType}
                                disabled={!startDate || !agreement}
                            />
                            {(!isDisabled && !!paymentSelected) && (
                                <SummaryBox
                                    className="mb-12"
                                    contentClassName="p-8"
                                    isFormBox
                                >
                                    <Preloader isLoading={isRefetching} overflow showInitiallyOverflow>
                                        <PaymentSummary
                                            deposit={paymentSelected.deposit}
                                            subtext={
                                                paymentType === "Monthly"
                                                    ? t("tuitionBilling.payMonthly")
                                                    : t(isRollingSelected ? "tuitionBilling.payHalfTermly" : "tuitionBilling.payInFull")
                                            }
                                            onePaymentSum={paymentSelected.oneOffAmount}
                                            discounts={paymentSelected.discounts}
                                            techFee={paymentSelected.techFee}
                                            total={paymentSelected.amountDue}
                                            studentFullName={studentFullName}
                                            onProceed={onProceed}
                                            reload={refetch}
                                        />
                                    </Preloader>
                                </SummaryBox>
                            )}
                        </div>
                    )}
                    {shouldPay && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutForm
                                studentFullName={studentFullName}
                                selectedType={paymentType}
                                isOnRollingContract={isRollingSelected}
                                invoice={payment?.invoice}
                                instalment={payment?.recurringAmount}
                            />
                        </Elements>
                    )}
                    {wasPaidInInvoice && (
                        <div className="checkout-status checkout-status-success mt-8">
                            <span className="checkout-status__icon checkout-status__icon-success">
                                <DoneIcon />
                            </span>
                            <div>
                                <Typography variant="h2" component="div" className="">
                                    {t("tuitionBilling.succeededMessage")}
                                </Typography>
                                <Typography variant="body1" component="div" className="checkout-status__subtext">
                                    {t("tuitionBilling.succeededMessageSub", { student: studentFullName })}
                                </Typography>
                            </div>
                        </div>
                    )}
                    {hasPaymentComplete && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus />
                        </Elements>
                    )}
                    {(isPaymentProcessing && !hasPaymentComplete) && (
                        <Elements options={stripeOptions} stripe={stripePromise}>
                            <CheckoutStatus isProcessing />
                        </Elements>
                    )}
                </Preloader>
            </LockedPage>
            <SnackbarToast
                open={!!errorConfigure}
                isError={!!errorConfigure}
                onClose={() => resetConfigure()}
            >
                {`${t("tuitionBilling.errorProceedMessage")} (status ${(errorConfigure)?.response?.status})`}
            </SnackbarToast>
        </div>
    );
};

export default TuitionBilling;
